import { ArchiveRestore, ChefHat, Edit, LineChart, Package, Trash2 } from "lucide-react";

import { barraEstoque, custoFicha, fmtBRL, fmtQtd, saldoTexto, unidadeResumo } from "./lib/item";

const TOM = {
  Baixo: { texto: "text-red-600 dark:text-red-400", barra: "bg-red-500", pill: "bg-red-500/10 text-red-600 dark:text-red-400", rotulo: "abaixo do mínimo" },
  Alto: { texto: "text-blue-600 dark:text-blue-400", barra: "bg-blue-500", pill: "bg-blue-500/10 text-blue-600 dark:text-blue-400", rotulo: "acima do máximo" },
  Normal: { texto: "text-emerald-600 dark:text-emerald-400", barra: "bg-emerald-500", pill: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", rotulo: "normal" },
};

const Icone = ({ onClick, titulo, children, testId, className = "" }) => (
  <button type="button" onClick={onClick} title={titulo} data-testid={testId}
    className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-primary hover:bg-primary/10 ${className}`}>
    {children}
  </button>
);

/** Uma linha da tabela de estoque: cada celula tem a informacao principal em
 *  cima e o detalhe embaixo (duas linhas), com a barra min→max no saldo. */
export default function LinhaItemEstoque({ item, status, ehReceita, canEdit, zebra, arquivado = false, onHistorico, onMovimentar, onEditar, onExcluir, onReativar }) {
  const un = unidadeResumo(item);
  const custo = custoFicha(item);
  const saldo = saldoTexto(item);
  const barra = barraEstoque(item);
  const tom = TOM[status?.label] || TOM.Normal;
  const min = Number(item.stock_min) || 0, max = Number(item.stock_max) || 0;
  const baixo = status?.label === "Baixo";

  return (
    <tr data-testid={`ingredient-row-${item.id}`} data-e2e="linha-estoque"
      className={`border-b align-top transition-colors hover:bg-primary/5 ${arquivado ? "opacity-70" : ""} ${ehReceita ? "bg-primary/5" : zebra ? "bg-muted/10" : ""} ${baixo && !arquivado ? "border-l-2 border-l-red-500/60" : ""}`}>
      <td className="py-3 px-4 font-mono text-xs text-muted-foreground whitespace-nowrap">{item.code || "—"}</td>

      <td className="py-3 px-4 min-w-[220px]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold truncate">{item.name}</span>
          {ehReceita && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary shrink-0"><ChefHat className="w-3 h-3" /> Receita</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-muted text-[11px] font-medium" data-e2e="chip-unidade">
            {un.mesma ? `compra e ficha em ${un.uso}` : `compra ${un.compra} · ficha ${un.uso}`}
          </span>
          {un.detalhe && <span className="text-[11px] text-muted-foreground">{un.detalhe}</span>}
        </div>
      </td>

      <td className="py-3 px-4 min-w-[200px]">
        <div className="flex items-baseline gap-2">
          <span className={`text-lg font-bold tabular-nums leading-none ${tom.texto}`} data-e2e="saldo">{saldo.principal}</span>
          {saldo.secundario && <span className="text-[11px] text-muted-foreground tabular-nums">{saldo.secundario}</span>}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="relative h-1.5 flex-1 rounded-full bg-muted overflow-hidden" title={barra.temFaixa ? `${Math.round(barra.progresso)}% do máximo` : "sem mínimo/máximo"}>
            {barra.temFaixa && <div className={`h-full ${tom.barra} transition-all`} style={{ width: `${barra.progresso}%` }} />}
            {barra.temFaixa && barra.marcaMin != null && <span className="absolute top-0 h-full w-px bg-foreground/40" style={{ left: `${barra.marcaMin}%` }} title="mínimo" />}
          </div>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${barra.temFaixa ? tom.pill : "bg-muted text-muted-foreground"}`} data-e2e="status">
            {barra.temFaixa ? tom.rotulo : "sem mín/máx"}
          </span>
        </div>
      </td>

      <td className="py-3 px-4 whitespace-nowrap">
        <button type="button" onClick={onEditar} disabled={!canEdit} title={canEdit ? "Editar mínimo e máximo" : ""} data-e2e="min-max"
          className="text-left hover:text-primary disabled:cursor-default">
          {barra.temFaixa ? (
            <>
              <span className="font-semibold tabular-nums">{fmtQtd(min, item.unit)} – {max > 0 ? fmtQtd(max, item.unit) : "—"}</span>
              <span className="text-[11px] text-muted-foreground"> {item.unit}</span>
              <div className="text-[11px] text-muted-foreground">mín – máx</div>
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground">definir mín/máx</span>
          )}
        </button>
      </td>

      <td className="py-3 px-4 min-w-[200px]">
        {custo ? (
          <>
            <p className="font-bold tabular-nums leading-none" data-e2e="custo">{fmtBRL(custo.valor)} <span className="text-[11px] font-medium text-muted-foreground">/{custo.unidade}</span></p>
            <p className="mt-1 text-[11px] text-muted-foreground" data-e2e="conta">{custo.conta}</p>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">sem compra registrada</span>
        )}
      </td>

      <td className="py-2 px-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-0.5">
          <Icone onClick={onHistorico} titulo="Histórico de preços e análise"><LineChart className="w-4 h-4" strokeWidth={1.8} /></Icone>
          {!canEdit ? <span className="text-[11px] text-muted-foreground">somente leitura</span> : arquivado ? (
            <>
              <button type="button" onClick={onReativar} data-testid={`unarchive-ingredient-${item.id}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium hover:border-primary/60 hover:text-primary transition-colors">
                <ArchiveRestore className="w-3.5 h-3.5" /> Reativar
              </button>
              <Icone onClick={onExcluir} titulo="Excluir definitivamente" testId={`delete-ingredient-${item.id}`} className="text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" strokeWidth={1.6} /></Icone>
            </>
          ) : (
            <>
              <Icone onClick={onMovimentar} titulo="Movimentar estoque" testId={`stock-ingredient-${item.id}`}><Package className="w-4 h-4" strokeWidth={1.6} /></Icone>
              <Icone onClick={onEditar} titulo="Editar" testId={`edit-ingredient-${item.id}`}><Edit className="w-4 h-4" strokeWidth={1.6} /></Icone>
              <Icone onClick={onExcluir} titulo="Excluir" testId={`delete-ingredient-${item.id}`} className="text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" strokeWidth={1.6} /></Icone>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
